import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';

const podPricing = [
  {
    pod: 1,
    ages: '1–2',
    termFee: 145000,
    description: 'Foundational sensory exploration and early language development',
  },
  {
    pod: 2,
    ages: '3–5',
    termFee: 340000,
    description: 'Pre-literacy, numeracy foundations and social-emotional growth',
  },
  {
    pod: 3,
    ages: '6–8',
    termFee: 400000,
    description: 'Core literacy, mathematical thinking and inquiry skills',
  },
  {
    pod: 4,
    ages: '9–11',
    termFee: 500000,
    description: 'Advanced reasoning, research projects and leadership development',
  },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function Fees() {
  return (
    <div data-testid="fees-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                Investment
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                Fees Structure
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-[#2D3A3A] max-w-2xl">
                An investment in mastery-driven education. Transparent pricing across all four learning pods.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pod Pricing Cards */}
      <section data-testid="pod-pricing-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <ScrollReveal>
            <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center mb-6">
              Termly Fees by Pod
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-center text-[#5C6B6B] font-light mb-16 max-w-xl mx-auto">
              Three terms per academic year. Fees cover all core learning materials and experiences.
            </p>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {podPricing.map((pod, index) => (
              <ScrollReveal key={pod.pod} delay={index * 100}>
                <div 
                  data-testid={`pod-${pod.pod}-card`}
                  className="bg-white rounded-2xl border border-[#EBE8E0] overflow-hidden h-full flex flex-col hover:shadow-lg transition-shadow duration-300"
                >
                  {/* Pod Header */}
                  <div className="p-6 bg-[#1A3C34]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#F9F8F4]/60 text-sm font-medium uppercase tracking-wider">
                        Pod {pod.pod}
                      </span>
                      <span className="bg-[#C27856] text-white text-xs font-medium px-3 py-1 rounded-full">
                        Ages {pod.ages}
                      </span>
                    </div>
                  </div>
                  
                  {/* Pricing */}
                  <div className="p-6 flex-grow">
                    <div className="mb-4">
                      <p className="text-sm text-[#5C6B6B] font-medium uppercase tracking-wider mb-1">
                        Per Term
                      </p>
                      <p className="text-3xl font-heading font-light text-[#1A3C34]">
                        KES {formatCurrency(pod.termFee)}
                      </p>
                    </div>
                    
                    <p className="text-sm text-[#5C6B6B] font-light leading-relaxed">
                      {pod.description}
                    </p>
                  </div>
                  
                  {/* Annual Calculation */}
                  <div className="p-6 bg-[#F9F8F4] border-t border-[#EBE8E0]">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#5C6B6B]">Annual (3 terms)</span>
                      <span className="font-medium text-[#1A3C34]">
                        KES {formatCurrency(pod.termFee * 3)}
                      </span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deposit Section */}
      <section data-testid="deposit-section" className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-2xl mx-auto">
            <ScrollReveal>
              <div className="bg-[#1A3C34] rounded-2xl overflow-hidden">
                <div className="p-8 md:p-12">
                  <h3 className="font-heading text-sm font-medium uppercase tracking-wider text-[#F9F8F4]/70">
                    Founding Cohort Deposit
                  </h3>
                  <p className="text-4xl md:text-5xl font-heading font-light text-[#F9F8F4] mt-4">
                    KES 200,000
                  </p>
                  <p className="text-base text-[#F9F8F4]/70 mt-4 font-light">
                    Secures your place in the September 2026 Founding Cohort. Deducted from first term fees.
                  </p>
                  <div className="mt-8 pt-6 border-t border-[#F9F8F4]/20">
                    <p className="text-sm text-[#F9F8F4]/60">
                      Refundable only if Nova Roots does not open as scheduled.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section data-testid="payment-options-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center mb-16">
                Payment Options
              </h2>
            </ScrollReveal>
            
            <div className="grid md:grid-cols-2 gap-8">
              <ScrollReveal delay={100}>
                <div className="bg-white rounded-2xl p-8 border border-[#EBE8E0] h-full">
                  <div className="w-12 h-12 bg-[#1A3C34] rounded-full flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-[#F9F8F4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-heading text-xl font-medium text-[#1A3C34] mb-3">
                    Annual Payment
                  </h3>
                  <p className="text-[#5C6B6B] font-light text-sm leading-relaxed mb-4">
                    Pay the full annual fee upfront and receive a 2% administrative credit.
                  </p>
                  <p className="text-[#C27856] font-medium text-sm">
                    2% credit applied
                  </p>
                </div>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <div className="bg-white rounded-2xl p-8 border border-[#EBE8E0] h-full">
                  <div className="w-12 h-12 bg-[#C27856] rounded-full flex items-center justify-center mb-6">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </div>
                  <h3 className="font-heading text-xl font-medium text-[#1A3C34] mb-3">
                    Termly Instalments
                  </h3>
                  <p className="text-[#5C6B6B] font-light text-sm leading-relaxed mb-4">
                    Spread payments across three terms. Due before the start of each term.
                  </p>
                  <p className="text-[#5C6B6B] font-medium text-sm">
                    Standard pricing
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section data-testid="included-section" className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center mb-16">
                What's Included
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'All core learning materials',
                  'Mastery-based assessments',
                  'Termly project resources',
                  'Agricultural programme participation',
                  'Technology and AI tools access',
                  'Progress reports and conferences',
                  'Small group instruction (8:1 ratio)',
                  'Personalised learning pathways',
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 py-3"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#C27856] flex-shrink-0" />
                    <span className="text-[#2D3A3A] font-light">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="mt-12 text-center text-[#5C6B6B] font-light text-sm">
                Additional costs may apply for optional field trips, uniform and specialised equipment.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#F9F8F4] max-w-xl mx-auto">
              Ready to Invest in Your Child's Future?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <p className="mt-6 text-base font-light text-[#F9F8F4]/70 max-w-md mx-auto">
              32 places available for the Founding Cohort. September 2026.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/enquire" 
                className="inline-block bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
                data-testid="fees-enquire-btn"
              >
                Submit Enquiry
              </Link>
              <Link 
                to="/admissions" 
                className="inline-block border border-[#F9F8F4]/30 text-[#F9F8F4] hover:bg-[#F9F8F4]/10 px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
                data-testid="fees-admissions-btn"
              >
                View Admissions Process
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
